import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './DigitalCI.css';
import './CardModal.css';
import { motion } from 'framer-motion';
import BlogModal from './BlogModal.jsx';
import { useParams, useLocation } from 'react-router-dom';
import Speedometer from './Speedometer.jsx';
import CardModal from './CardModal';
import emojiDictionary from 'utils/emoji';
import { fetchWithAuth, handleApiError } from 'helpers/fetchWithAuth';
import {
  BASE_ENERGY_OPTIONS,
  CAD_GENERIC,
  CAD_HIGH_TEMP,
  ALL_ENERGY_OPTIONS,
} from 'constants/energySourceOptions';
// import BuildingGallery from 'components/IncitativeConsulting/BuildingGallery.jsx';
import ci_mockup_data from 'data/ci_digital_mockup.json';

/** ===== Helpers nombre / affichage ===== **/
export const isNumeric = (v) => {
  if (v === null || v === undefined) return false;
  const s = String(v).trim().replace(/\s/g, '');
  return s !== '' && !isNaN(Number(s));
};

const toNumber = (v, fallback = 0) =>
  isNumeric(v) ? Number(String(v).replace(/\s/g, '')) : fallback;

/** Ajoute des ' toutes les 3 unités, sans toucher la partie décimale */
export function formatNumber(value, round) {
  if (!isNumeric(value)) return '—';
  const num = Number(String(value).replace(/\s/g, ''));
  const rounded = round ? Math.round(num / round) * round : Math.ceil(num);
  const int = String(rounded);
  const intFmt = int.replace(/\B(?=(\d{3})+(?!\d))/g, "'");
  return intFmt;
}

export const chf = (v, round) =>
  isNumeric(v) ? `CHF ${formatNumber(v, round)}` : 'CHF —';
const kwhAn = (v) => (isNumeric(v) ? `${formatNumber(v)} kWh/an` : '—');
const kgCo2An = (v) => (isNumeric(v) ? `${formatNumber(v)} kg CO₂/an` : '—');

const CheckIcon = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" role="img" aria-label="OK">
    <path
      d="M20 6L9 17l-5-5"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// IDC speedometer: 300 kWh/m²·an is the upper bound of the scale
const IDC_SCALE_MAX = 300;

// CO2 equivalent: avg Geneva–New York round-trip ~1 200 kg CO₂
const KG_CO2_PER_FLIGHT_GVA_NYC = 1200;

// Energy content conversion factors (kWh per unit)
const KWH_PER_LITRE_OIL = 10; // mazout
const KWH_PER_M3_GAS = 10.33; // gaz naturel
const KWH_PER_KG_PELLETS = 4.8; // granulés de bois

/** ===== Potentiels énergétiques : rendu dynamique à partir d'un dict de booléens ===== **/
function renderPotentials(p, t) {
  if (!p) return null;
  const mapping = [
    { key: 'hpAir' },
    { key: 'hpGround' },
    { key: 'hpWater' },
    { key: 'woodPellets' },
    { key: 'districtHeatingGeneric' },
  ];

  const iconMap = {
    hpAir: <span aria-hidden>&#x1F32C;&#xFE0F;&#x1F4A7;</span>,
    hpGround: <span aria-hidden>&#x1FAA8;&#x1F4A7;</span>,
    hpWater: <span aria-hidden>&#x1F4A7;&#x1F3DE;&#xFE0F;</span>,
    woodPellets: <span aria-hidden>&#x1F33E;&#x1F525;</span>,
    districtHeatingGeneric: <span aria-hidden>&#x2668;&#xFE0F;&#x1F517;</span>,
  };

  const statusNode = (k) => {
    if (p[`${k}Possibility`])
      return (
        <span className="status status--ok">
          {t('potentiels.legend.possible')}
        </span>
      );
    if (p[`${k}Impossibility`])
      return (
        <span className="status status--no">
          {t('potentiels.legend.impossible')}
        </span>
      );
    return (
      <span className="status status--maybe">
        {t('potentiels.legend.toStudy')}
      </span>
    );
  };

  const tagsNode = (k) => {
    const tags = [];
    if (p[`${k}SolarCombination`])
      tags.push(
        <li key={`${k}-pv`} className="tag tag--pv">
          {t('potentiels.tags.pvCombination')}
        </li>,
      );
    if (p[`${k}SolarPertinent`])
      tags.push(
        <li key={`${k}-st`} className="tag tag--st">
          {t('potentiels.tags.solarThermal')}
        </li>,
      );
    return tags.length ? <ul className="tags">{tags}</ul> : null;
  };

  return (
    <div className="potentials__grid">
      {mapping.map((m) => (
        <article key={m.key} className="pot-card" id={m.key}>
          <div className="pot-card__head">
            <div className="pot-card__icon" aria-hidden>
              {iconMap[m.key]}
            </div>
            <h3 className="pot-card__title">
              {t(`potentiels.cards.${m.key}.title`)}
            </h3>
          </div>
          <div className="pot-card__status">{statusNode(m.key)}</div>
          {tagsNode(m.key)}
          <p className="pot-card__note">
            {t(`potentiels.cards.${m.key}.note`)}
          </p>
        </article>
      ))}
    </div>
  );
}

/** ===== Composant principal ===== **/
export default function DigitalCI() {
  // Exemple de données mock pour ciData et meta
  const { mandateId: CECBPlusIdFromUrl } = useParams();
  const [ciData, setCiData] = useState({});
  function useQuery() {
    return new URLSearchParams(useLocation().search);
  }

  const q = useQuery();
  const token = q.get('token');

  useEffect(() => {
    const fetchCiData = async () => {
      try {
        const { data } = await fetchWithAuth(
          'Fetching CI data',
          `/incitative-consulting/${CECBPlusIdFromUrl}/get-ci-data?token=${token}`,
        );
        setCiData(data || {});
      } catch (error) {
        handleApiError(error, 'Failed to fetch CI data');
      }
    };

    const current_url = window.location.pathname;
    if (current_url.includes('/digitalci-mockup')) {
      setCiData(ci_mockup_data);
    } else {
      fetchCiData();
    }
  }, [CECBPlusIdFromUrl, token]);

  const { t } = useTranslation('DigitalCI');

  const [openModalId, setOpenModalId] = useState(null);
  const [openCardModalId, setOpenCardModalId] = useState(null);
  const meta = {
    pdfUrl: ciData.pdfPresignedUrl || 'i-neea.ch',
    version: '1.0.0',
    renderedAt: '2024-06-01',
  };

  // Normalized accessors for the new ciData shape
  const object = ciData?.object || {};
  const heatingSystem = ciData?.heatingSystem || {};
  const energeticContext = ciData?.energeticContext || {};
  const consumption = ciData?.consumption || {};
  const newSystem = ciData?.newSystem || {};

  // Données principales
  const addressLine = [
    object.address || ciData?.object?.address,
    object.zip || object.postalCode || ciData?.object?.zip,
    object.city || ciData?.object?.city,
  ]
    .filter(Boolean)
    .join(', ');

  // KPIs
  const conso = toNumber(consumption.total);

  const coutAnnuel = toNumber(consumption.energyCosts);

  const idcVal = toNumber(consumption.idc);
  const idcDisplay = idcVal !== null ? formatNumber(idcVal) : '—';

  const idcRatio =
    idcVal === null ? 0 : Math.min((idcVal / IDC_SCALE_MAX) * 100, 100);

  // Impact CO2 actuel (si dispo) + nature fossile
  const mainSource = String(
    (
      heatingSystem.mainHeatSource ||
      heatingSystem.energySource ||
      ''
    ).toLowerCase(),
  );
  const isFossil = [
    'gas',
    'gaz',
    'oil',
    'mazout',
    'fuel',
    'fioul',
    'coal',
    'gasheizung',
  ].includes(mainSource);
  const fossilPct = isFossil ? 100 : 0;
  const co2Actuel = newSystem.co2Saving ?? null;

  const newType =
    [...BASE_ENERGY_OPTIONS, CAD_GENERIC].find(
      (o) => o.value === newSystem.energySource,
    )?.fallback ?? newSystem.energySource;

  const currentEnergySource =
    ALL_ENERGY_OPTIONS.find((o) => o.value === heatingSystem.energySource)
      ?.fallback ?? heatingSystem.energySource;

  const newPower = isNumeric(newSystem.power)
    ? `${formatNumber(newSystem.power)} kW`
    : '—';

  const subvention = toNumber(
    newSystem.subsidies ?? newSystem.subvention ?? newSystem.subsidy ?? 0,
    null,
  );
  const coutEnergieNouveau = toNumber(newSystem.costEnergyMaintenance, null);

  // Économies = coût actuel - coût nouveau (fallback: saving si fourni)
  const coutActuelKnown = isNumeric(consumption.energyCosts)
    ? toNumber(consumption.energyCosts)
    : null;
  const economiesCalc =
    coutActuelKnown !== null && coutEnergieNouveau !== null
      ? coutActuelKnown - coutEnergieNouveau
      : isNumeric(ciData?.saving)
        ? toNumber(ciData.saving)
        : null;

  return (
    <div className="rapport">
      {/* Header */}
      <header className="rapport__header">
        <div className="container header__row">
          <div className="logo">
            <div className="logo__badge" aria-hidden>
              <img
                alt="logo i-neea"
                src="/logo_square.png"
                style={{ height: '3em' }}
              />
            </div>
            <div>
              <h1 className="logo__title">{t('header.title')}</h1>
              <p className="logo__subtitle">
                {addressLine || t('header.addressFallback')}
              </p>
            </div>
          </div>

          <nav className="nav">
            <a href="#accueil" className="nav__link">
              {t('header.nav.accueil')}
            </a>
            <a href="#etat-actuel" className="nav__link">
              {t('header.nav.etat')}
            </a>
            <a href="#contraintes" className="nav__link">
              {t('header.nav.contraintes')}
            </a>
            <a href="#potentiels" className="nav__link">
              {t('header.nav.potentiels')}
            </a>
            <a href="#recommandations" className="nav__link">
              {t('header.nav.recommandations')}
            </a>
            <a href="#conclusion" className="nav__link">
              {t('header.nav.conclusion')}
            </a>
          </nav>

          <div className="header__actions">
            <button
              className="btn"
              onClick={() => window.open(meta.pdfUrl, '_blank', 'noreferrer')}
              aria-label={t('header.downloadPdf')}
            >
              <span>⬇️ PDF</span>
            </button>
            {/* <button onClick={() => setOpen(true)}>Open settings</button>
            <CustomizeCI
              open={open}
              onClose={() => setOpen(false)}
              currentData={ciData}
              setValues={setCiData}
            /> */}
          </div>
        </div>
      </header>

      <main className="container rapport__main">
        {/* Hero intro (inserted) */}
        <section className="ci-hero">
          <div className="ci-hero__container">
            <h2 className="ci-hero__title">{t('hero.title')}</h2>
            <p className="ci-hero__subtitle">{t('hero.subtitle1')}</p>
            <p className="ci-hero__subtitle">{t('hero.subtitle2')}</p>
          </div>
        </section>
        {/* Accueil */}
        <section id="accueil" className="section">
          <div className="section__head">
            <h2 className="section__title">{t('accueil.title')}</h2>
            <p className="section__subtitle">{t('accueil.subtitle')}</p>
          </div>

          {/* Overview : infos à gauche, KPIs à droite */}
          <div className="overview-grid">
            {/* Informations du bâtiment */}
            <article className="ci-card building-info">
              <div className="card__head">
                <h3 className="card__title">
                  {t('accueil.buildingInfo.title')}
                </h3>
              </div>

              <div className="info-grid">
                <div className="sys-tile">
                  <div className="info-tile__label">
                    {t('accueil.buildingInfo.sre')}
                  </div>
                  <div className="sys-tile__value">
                    {isNumeric(consumption.sre)
                      ? `${formatNumber(consumption.sre)} m²`
                      : '—'}
                  </div>
                </div>
                <div className="sys-tile">
                  <div className="info-tile__label">
                    {t('accueil.buildingInfo.heatingYear')}
                  </div>
                  <div className="info-tile__value">
                    {isNumeric(heatingSystem.year) ? heatingSystem.year : '—'}
                  </div>
                </div>
                <div className="sys-tile">
                  <div className="info-tile__label">
                    {t('accueil.buildingInfo.housingUnits')}
                  </div>
                  <div className="info-tile__value">
                    {isNumeric(object.housingUnitCount)
                      ? formatNumber(object.housingUnitCount)
                      : '—'}
                  </div>
                </div>
                <div className="sys-tile">
                  <div className="info-tile__label">
                    {t('accueil.buildingInfo.constructionYear')}
                  </div>
                  <div className="info-tile__value">
                    {object.constructionYear}
                  </div>
                </div>
                <div className="sys-tile">
                  <div className="info-tile__label">
                    {t('accueil.buildingInfo.currentSystem')}
                  </div>
                  <div className="info-tile__value">{currentEnergySource}</div>
                </div>
                <div className="sys-tile">
                  <div className="info-tile__label">
                    {t('accueil.buildingInfo.currentPower')}
                  </div>
                  <div className="info-tile__value">
                    {heatingSystem.power ? String(heatingSystem.power) : '—'} kW
                  </div>
                </div>
              </div>
            </article>

            {/* KPI cards */}
            <aside className="kpi-cards">
              <motion.article
                layoutId="annual-consumption"
                className="ci-card card-article"
              >
                <div className="kpi-card__head">
                  <h3 className="card__title">
                    {t('accueil.kpi.annualConsumption')}
                  </h3>
                  <div className="kpi-card__icon kpi-card__icon--orange">
                    🔥
                  </div>
                </div>
                <p className="kpi-card__value">{kwhAn(conso)}</p>
                <button
                  aria-label={t('help')}
                  className="card__help-button"
                  onClick={() => setOpenCardModalId('annual-consumption')}
                  type="button"
                >
                  ?
                </button>
              </motion.article>
              {openCardModalId === 'annual-consumption' && (
                <CardModal
                  layoutId={'annual-consumption'}
                  onClose={() => setOpenCardModalId(null)}
                  title={t('accueil.modal.consumption.title')}
                  leftContent={
                    <div>
                      <span className="unity">
                        {t('accueil.modal.consumption.unit')}
                      </span>
                      <div className="amount" id="costAmount">
                        {formatNumber(conso)}
                      </div>
                      <div className="small">
                        {t('accueil.modal.consumption.note')}
                      </div>
                    </div>
                  }
                  rightContent={
                    <aside className="details-section" aria-live="polite">
                      <h3 className="details-title">
                        {t('accueil.modal.consumption.whatIsKwh')}
                      </h3>
                      <p className="muted">
                        {t('accueil.modal.consumption.kwhExplain')}
                      </p>
                      <h4 className="details-subtitle">
                        {t('accueil.modal.consumption.equivalences')}
                      </h4>
                      <div className="small-list">
                        {heatingSystem.energySource === 'oil' && (
                          <p>
                            {t('accueil.modal.consumption.oil', {
                              value: Number(
                                (consumption.total / KWH_PER_LITRE_OIL).toFixed(
                                  1,
                                ),
                              ),
                            })}
                          </p>
                        )}
                        {heatingSystem.energySource === 'gas' && (
                          <p>
                            {t('accueil.modal.consumption.gas', {
                              value: Number(
                                (consumption.total / KWH_PER_M3_GAS).toFixed(1),
                              ),
                            })}
                          </p>
                        )}
                        {heatingSystem.energySource === 'wood_pellets' && (
                          <p>
                            {t('accueil.modal.consumption.pellets', {
                              value: Number(
                                (
                                  consumption.total / KWH_PER_KG_PELLETS
                                ).toFixed(1),
                              ),
                            })}
                          </p>
                        )}
                      </div>
                      <div className="detail-item">
                        <div className="small muted">
                          {t('accueil.modal.consumption.disclaimer')}
                        </div>
                      </div>
                    </aside>
                  }
                />
              )}

              <motion.article
                layoutId="annual-energy-cost"
                className="ci-card card-article"
              >
                <div className="kpi-card__head">
                  <h3 className="card__title">
                    {t('accueil.kpi.annualExpenses')}
                  </h3>
                  <div className="kpi-card__icon kpi-card__icon--blue">💰</div>
                </div>
                <div className="kpi-card__body">
                  <p className="kpi-card__value">{chf(coutAnnuel)}</p>
                </div>
                <button
                  aria-label={t('help')}
                  className="card__help-button"
                  onClick={() => setOpenCardModalId('annual-energy-cost')}
                  type="button"
                >
                  ?
                </button>
              </motion.article>
              {openCardModalId === 'annual-energy-cost' && (
                <CardModal
                  layoutId={'annual-energy-cost'}
                  onClose={() => setOpenCardModalId(null)}
                  title={t('accueil.modal.energyCost.title')}
                  leftContent={
                    <div>
                      <span className="unity">
                        {t('accueil.modal.energyCost.unit')}
                      </span>
                      <div className="amount" id="costAmount">
                        {chf(coutActuelKnown)}
                      </div>
                      <div className="small">
                        {t('accueil.modal.energyCost.note')}
                      </div>
                    </div>
                  }
                  rightContent={
                    <div className="details-section">
                      <div className="detail-item">
                        <div className="detail-label">
                          {t('accueil.modal.energyCost.labelConsumption')}
                        </div>
                        <div className="detail-value">{kwhAn(conso)}</div>
                      </div>
                      <div className="detail-item">
                        <div className="detail-label">
                          {t('accueil.modal.energyCost.labelPrice')}
                        </div>
                        <div className="detail-value">
                          {heatingSystem.energyPriceChfPerKwh} CHF / kWh
                        </div>
                      </div>
                      <div className="detail-item">
                        <div className="detail-label">
                          {t('accueil.modal.energyCost.labelBill')}
                        </div>
                        <div className="detail-value">
                          {t('accueil.modal.energyCost.billFormula')}
                        </div>
                      </div>
                    </div>
                  }
                />
              )}

              {object.canton === 'GE' ? (
                <motion.article className="ci-card kpi-card--warning">
                  <div className="kpi-card__head">
                    <span className="card__title">{t('accueil.kpi.idc')}</span>
                    <div className="kpi-card__icon kpi-card__icon--alert">
                      ⚠️
                    </div>
                  </div>
                  <p className="kpi-card__value">{idcDisplay} kWh/m²·an</p>
                  {idcDisplay !== '—' && idcDisplay > 125 && (
                    <p className="kpi-card__note">
                      {t('accueil.kpi.idcWarning')}
                    </p>
                  )}
                </motion.article>
              ) : null}
            </aside>
          </div>
        </section>
        {/* État énergétique actuel */}
        {/* {object.canton === 'GE' ? ( */}
        <section id="etat-actuel" className="section">
          <h2 className="section__title">{t('etatActuel.title')}</h2>
          <div className="grid grid--2">
            <article className="ci-card">
              <h3 className="card__title">{t('etatActuel.idc.title')}</h3>
              <Speedometer needlePercentage={idcRatio} />
              <p className="impact-co2__amount">{idcDisplay} kWh/m²·an</p>
            </article>

            <article className="ci-card impact-co2">
              <h3 className="card__title">{t('etatActuel.co2.title')}</h3>
              <div className="impact-co2__content">
                <div className="impact-co2__circle">
                  <div>
                    <div className="impact-co2__circle-value">{fossilPct}%</div>
                    <div className="impact-co2__circle-label">
                      {isFossil
                        ? t('etatActuel.co2.fossil')
                        : t('etatActuel.co2.nonFossil')}
                    </div>
                  </div>
                </div>
                <p className="impact-co2__amount">
                  {co2Actuel ? kgCo2An(co2Actuel) : '—'}
                </p>
                <div className="impact-co2__alert">
                  <p>
                    {t('etatActuel.co2.flightEquiv', {
                      count: Math.round(co2Actuel / KG_CO2_PER_FLIGHT_GVA_NYC),
                    })}
                  </p>
                </div>
              </div>
            </article>
          </div>
          {/* Chaudières actuelles + illustration */}
          {/* <article className="card">
            <h3 className="card__title">Chaudières actuelles</h3>

            <div className="boiler-wrap">
              <div className="stack">
                <div className="stack__row">
                  <div className="stack__icon" aria-hidden>
                    🔥
                  </div>
                  <div>
                    <p className="stack__title">Chaudière principale</p>
                    <p className="stack__subtitle">
                      Puissance installée :{' '}
                      {isNumeric(ciData.installedHeatingPower)
                        ? `${formatNumber(ciData.installedHeatingPower)} kW`
                        : '—'}
                    </p>
                  </div>
                </div>

                <div className="stack__row">
                  <div className="stack__icon" aria-hidden>
                    🔥
                  </div>
                  <div>
                    <p className="stack__title">Systèmes en place</p>
                    <p className="stack__subtitle">
                      Nombre d'unités :{' '}
                      {isNumeric(ciData.heatSysCount)
                        ? formatNumber(ciData.heatSysCount)
                        : '—'}
                    </p>
                  </div>
                </div>
              </div>

              <div className="placeholder boiler-illustration">
                <div className="placeholder__icon" aria-hidden>
                  🛠️
                </div>
                <p className="placeholder__text">
                  Illustration chaufferie / schéma hydraulique
                </p>
              </div>
            </div>
          </article> */}
        </section>

        {/* Building gallery */}
        {/* {CECBPlusIdFromUrl ? (
          <BuildingGallery
            mandateId={CECBPlusIdFromUrl}
            photos={ciData.pictures}
          />
        ) : null} */}

        {/* ) : null} */}
        {/* Contraintes légales */}
        <section id="contraintes" className="section contraintes">
          <h2 className="section__title">{t('contraintes.title')}</h2>

          <motion.article layoutId="timeline" className="timeline-card">
            <h3 className="card__title">{t('contraintes.timeline.title')}</h3>
            <div className="timeline">
              <div className="timeline__line" aria-hidden />
              <div className="timeline__items">
                <div className="timeline__item">
                  <div className="timeline__dot timeline__dot--red" aria-hidden>
                    <span>{emojiDictionary.prohibition}</span>
                  </div>
                  <div className="timeline__box timeline__box--red">
                    <p className="timeline__box-title">
                      {t('contraintes.timeline.fossil2023Title')}
                    </p>
                    <p className="timeline__box-sub">
                      {t('contraintes.timeline.fossil2023Sub')}
                    </p>
                  </div>
                </div>
                <div className="timeline__item">
                  <div
                    className="timeline__dot timeline__dot--blue"
                    aria-hidden
                  >
                    <span
                      style={{
                        display: 'inline-block',
                        transform: 'translateY(-2px) translateX(1px)',
                      }}
                    >
                      {emojiDictionary.objective}
                    </span>
                  </div>
                  <div
                    className="timeline__box timeline__box--blue"
                    onClick={() => setOpenModalId('modal_idc_goal')}
                  >
                    <p className="timeline__box-title">
                      {t('contraintes.timeline.idc2022Title')}
                    </p>
                    <p className="timeline__box-sub">
                      {idcDisplay !== '—'
                        ? t('contraintes.timeline.idcCurrent', {
                            value: idcDisplay,
                          })
                        : '—'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <button
              aria-label={t('help')}
              className="card__help-button"
              onClick={() => setOpenCardModalId('timeline')}
              type="button"
            >
              ?
            </button>
          </motion.article>
          {openCardModalId === 'timeline' && (
            <CardModal
              layoutId={'timeline'}
              onClose={() => setOpenCardModalId(null)}
              title={t('etatActuel.modal.title')}
              leftContent={
                <div>
                  <span className="unity">{t('etatActuel.modal.unit')}</span>
                  <div className="amount" id="costAmount">
                    125
                  </div>
                  <div className="small">{t('etatActuel.modal.goal')}</div>
                </div>
              }
              rightContent={
                <div className="details-section">
                  <h3 className="details-title">
                    {t('etatActuel.modal.below125Title')}
                  </h3>
                  <p className="muted">{t('etatActuel.modal.below125')}</p>
                  <h3 className="details-title">
                    {t('etatActuel.modal.between125and222Title')}
                  </h3>
                  <p className="muted">
                    {t('etatActuel.modal.between125and222')}
                  </p>
                  <h3 className="details-title">
                    {t('etatActuel.modal.above222Title')}
                  </h3>
                  <p className="muted">{t('etatActuel.modal.above222')}</p>
                </div>
              }
            />
          )}

          <article className="info-card">
            <div
              className="info-card__row"
              onClick={() => setOpenModalId('modal_idc')}
              style={{ cursor: 'pointer' }}
            >
              <div className="info-card__icon" aria-hidden>
                ℹ️
              </div>
              <div>
                <h3 className="info-card__title">
                  {t('contraintes.idcInfo.title')}
                </h3>
                <p className="info-card__text">
                  {t('contraintes.idcInfo.text')}
                </p>
              </div>
            </div>
          </article>

          {isNumeric(object.constructionYear) &&
            object.constructionYear < 1995 && (
              <article className="info-card info-card--warning">
                <div className="info-card__row">
                  <div className="info-card__icon" aria-hidden>
                    🏚️
                  </div>
                  <div>
                    <h3 className="info-card__title">
                      {t('contraintes.renovation.title')}
                    </h3>
                    <p className="info-card__text">
                      {t('contraintes.renovation.text')}
                    </p>
                  </div>
                </div>
              </article>
            )}
        </section>

        <section id="potentiels" className="section potentials">
          <h2 className="section__title">{t('potentiels.title')}</h2>
          <p className="section__subtitle">{t('potentiels.subtitle')}</p>

          <div className="legend">
            <span className="legend__item">
              <span className="pill pill--ok" />{' '}
              {t('potentiels.legend.possible')}
            </span>
            <span className="legend__item">
              <span className="pill pill--no" />{' '}
              {t('potentiels.legend.impossible')}
            </span>
            <span className="legend__item">
              <span className="pill pill--maybe" />{' '}
              {t('potentiels.legend.toStudy')}
            </span>
          </div>

          {renderPotentials(energeticContext, t)}
        </section>

        <section id="recommandations" className="section new-system">
          <h2 className="section__title">{t('recommandations.title')}</h2>
          <p className="section__subtitle">{t('recommandations.subtitle')}</p>

          <article className="system-card">
            <div className="system-card__head">
              <div className="system-card__icon" aria-hidden>
                &#x1F32C;&#xFE0F;&#x1F4A7;
              </div>
              <div>
                <h3 className="system-card__title">
                  {newType} — {newPower}
                </h3>
                <p className="system-card__subtitle">
                  {t('recommandations.renewablePriority')}
                </p>
              </div>
            </div>

            <div className="system-grid">
              <div className="sys-tile sys-tile--energy">
                <div className="sys-tile__label">
                  {t('recommandations.budget')}
                </div>
                <div className="sys-tile__value">
                  {chf(newSystem.costInvestment)}
                </div>
              </div>

              <div
                className="sys-tile sys-tile--subsidy"
                onClick={() => setOpenModalId('modal_subsidy')}
              >
                <div className="sys-tile__label">
                  {t('recommandations.subsidy')}
                </div>
                <div className="sys-tile__value">{chf(subvention)}</div>
                <BlogModal
                  isOpen={openModalId === 'modal_subsidy'}
                  onClose={() => setOpenModalId(null)}
                  title={t('recommandations.subsidyModal.title')}
                  logoSrc="https://www.i-neea.ch/favicon-32x32.png"
                  description={t('recommandations.subsidyModal.description')}
                  ctaText={t('recommandations.subsidyModal.cta')}
                  ctaHref="https://www.i-neea.ch/"
                />
              </div>

              <div className="sys-tile sys-tile--energy">
                <div className="sys-tile__label">
                  {t('recommandations.newEnergyBill')}
                </div>
                <div className="sys-tile__value">{chf(coutEnergieNouveau)}</div>
              </div>

              <div className="sys-tile sys-tile--savings">
                <div className="sys-tile__label">
                  {t('recommandations.annualSavings')}
                </div>
                <div className="sys-tile__value text-green">
                  {chf(economiesCalc)}
                </div>
              </div>

              <div className="sys-tile sys-tile--co2saved">
                <div className="sys-tile__label">
                  {t('recommandations.co2Avoided')}
                </div>
                <div className="sys-tile__value">
                  {kgCo2An(newSystem.co2Saving)}
                </div>
              </div>
            </div>
          </article>
        </section>

        <section id="conclusion" className="ci-card">
          <article className="sys-tile">
            <div className="note__icon" aria-hidden>
              🧭
            </div>
            <div>
              <h3 className="note__title">{t('conclusion.title')}</h3>
              <p>{t('conclusion.text1')}</p>
              <p>{t('conclusion.text2')}</p>
            </div>
          </article>
        </section>

        <section className="cta-cecb" aria-labelledby="cta-cecb-title">
          <div className="cta-cecb__container">
            <header className="cta-cecb__header">
              <h2 id="cta-cecb-title" className="cta-cecb__title">
                {t('cta.title')}
              </h2>
              <p className="cta-cecb__subtitle">{t('cta.subtitle')}</p>
            </header>

            <div className="cta-cecb__grid">
              <ul
                className="cta-cecb__benefits"
                aria-labelledby="cta-cecb-title"
              >
                <li className="cta-cecb__benefit">
                  <span className="cta-cecb__icon" aria-hidden="true">
                    <CheckIcon />
                  </span>
                  <span>
                    <strong>{t('cta.benefit1Bold')}</strong>{' '}
                    {t('cta.benefit1Rest')}
                  </span>
                </li>
                <li className="cta-cecb__benefit">
                  <span className="cta-cecb__icon" aria-hidden="true">
                    <CheckIcon />
                  </span>
                  <span>
                    <strong>{t('cta.benefit2Bold')}</strong>{' '}
                    {t('cta.benefit2Rest')}
                  </span>
                </li>
                <li className="cta-cecb__benefit">
                  <span className="cta-cecb__icon" aria-hidden="true">
                    <CheckIcon />
                  </span>
                  <span>
                    <strong>{t('cta.benefit3Bold')}</strong>{' '}
                    {t('cta.benefit3Rest')}
                  </span>
                </li>
              </ul>

              <aside className="cta-cecb__card" aria-label="Action">
                <p className="cta-cecb__pitch" />
                <div className="cta-cecb__actions">
                  <button type="button">{t('cta.contact')}</button>
                </div>
                <p className="cta-cecb__footnote">{t('cta.footnote')}</p>
              </aside>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}
